import { getExtensionValue, MedplumClient } from "@medplum/core";
import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";

import zip from "lodash/zip";

import { GYNECOLOGICAL_PHYSICAL_EXAMS } from "@/encounters/constants/gynecological-encounter";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Extensions } from "@/fhir/enums/extensions";
import {
  GynecologicalPhysicalExamsQuestionnaires,
  PhysicalExamQuestionnaires,
} from "@/fhir/enums/questionnaires";

export enum GynecologicalPhysicalExamAnswerType {
  SECTION = "gynecological-section",
  SINGLE_CHOICE = "gynecological-single-choice",
  NOTES = "gynecological-notes",
}

// Simple in-memory cache for processed diff data
const diffCache = new Map<
  string,
  { data: GynecologicalPhysicalExamDiffResult; timestamp: number }
>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Generate cache key based on parameters
function generateCacheKey(
  encounterId: string,
  versionId?: string,
  currentVersionIndex?: number,
): string {
  return `gynecological-diff:${encounterId}:${versionId || "null"}:${currentVersionIndex ?? "null"}`;
}

// Check if cache entry is still valid
function isCacheValid(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_TTL;
}

// Clear cache for a specific encounter (useful when data changes)
export function clearGynecologicalDiffCache(encounterId: string): void {
  const keysToDelete = Array.from(diffCache.keys()).filter((key) => key.includes(encounterId));
  keysToDelete.forEach((key) => diffCache.delete(key));
}

type PhysicalExamKey = keyof typeof GynecologicalPhysicalExamsQuestionnaires;
type GeneralPhysicalExamKey = keyof typeof PhysicalExamQuestionnaires;

// Function to get the correct questionnaire URL for each physical exam
function getPhysicalExamUrl(key: string): string | undefined {
  // First check if it's a gynecological-specific exam
  if (key in GynecologicalPhysicalExamsQuestionnaires) {
    return GynecologicalPhysicalExamsQuestionnaires[key as PhysicalExamKey];
  }

  // Then check if it's a general physical exam
  if (key in PhysicalExamQuestionnaires) {
    return PhysicalExamQuestionnaires[key as GeneralPhysicalExamKey];
  }

  return undefined;
}

// Processed format for gynecological physical exams
export interface GynecologicalPhysicalExamSectionAnswer {
  type: GynecologicalPhysicalExamAnswerType.SECTION;
  sectionName: string;
  normal: boolean;
  notNormal: boolean;
  declined: boolean;
  normalDetails: string[];
  notNormalDetails: string[];
  notes?: string;
}

export interface GynecologicalPhysicalExamSingleChoiceAnswer {
  type: GynecologicalPhysicalExamAnswerType.SINGLE_CHOICE;
  sectionName: string;
  declined: boolean;
  singleChoice: boolean;
  singleChoiceDetails: string[];
  notes?: string;
}

export interface GynecologicalPhysicalExamNotesAnswer {
  type: GynecologicalPhysicalExamAnswerType.NOTES;
  notes: string;
}

export type GynecologicalPhysicalExamAnswer =
  | GynecologicalPhysicalExamSectionAnswer
  | GynecologicalPhysicalExamSingleChoiceAnswer
  | GynecologicalPhysicalExamNotesAnswer
  | undefined;

export type GynecologicalPhysicalExamDiffResult = {
  [title: string]: GynecologicalPhysicalExamAnswer[][];
};

// Helper function to parse gynecological physical exam data
function parseGynecologicalPhysicalExamAnswers(
  questionnaireResponse?: QuestionnaireResponse,
): GynecologicalPhysicalExamAnswer[] {
  if (!questionnaireResponse?.item) return [];

  const answers: GynecologicalPhysicalExamAnswer[] = [];

  // Process each section in the questionnaire response
  for (const item of questionnaireResponse.item) {
    if (item.linkId === "notes") {
      // Handle general notes
      const notesValue = item.answer?.[0]?.valueString || "";
      if (notesValue) {
        answers.push({
          type: GynecologicalPhysicalExamAnswerType.NOTES,
          notes: notesValue,
        });
      }
      continue;
    }

    const sectionLinkId = item.linkId;
    const find = (linkId: string) => item.item?.find((subItem) => subItem.linkId === linkId);

    // Check if this is a single choice section
    const isSingleChoice = item.extension?.some(
      (ext) => ext.url === Extensions.PhysicalExamQuestionType && ext.valueString === "single",
    );

    if (isSingleChoice) {
      // Handle single choice sections (like Perineum Laceration, Episiotomy Type)
      answers.push({
        type: GynecologicalPhysicalExamAnswerType.SINGLE_CHOICE,
        sectionName: item.text || sectionLinkId,
        declined: find(`${sectionLinkId}Declined`)?.answer?.[0]?.valueBoolean ?? false,
        singleChoice: find(`${sectionLinkId}SingleChoice`)?.answer?.[0]?.valueBoolean ?? false,
        singleChoiceDetails:
          find(`${sectionLinkId}SingleChoiceDetails`)
            ?.answer?.map((a) => a.valueCoding?.display || a.valueCoding?.code)
            .filter((item): item is string => Boolean(item)) || [],
        notes: find(`${sectionLinkId}Notes`)?.answer?.[0]?.valueString || "",
      });
    } else {
      // Handle standard sections (like Head, Eyes, Ears, Vagina, Cervix, Uterus, etc.)
      // All physical exams use the same structure: sectionLinkId + Normal/NotNormal/Declined
      const normalValue = find(`${sectionLinkId}Normal`)?.answer?.[0]?.valueBoolean ?? false;
      const notNormalValue = find(`${sectionLinkId}NotNormal`)?.answer?.[0]?.valueBoolean ?? false;
      const declinedValue = find(`${sectionLinkId}Declined`)?.answer?.[0]?.valueBoolean ?? false;

      // Only include sections that have actual data
      if (normalValue || notNormalValue || declinedValue) {
        answers.push({
          type: GynecologicalPhysicalExamAnswerType.SECTION,
          sectionName: item.text || sectionLinkId,
          normal: normalValue,
          notNormal: notNormalValue,
          declined: declinedValue,
          normalDetails:
            find(`${sectionLinkId}NormalDetails`)
              ?.answer?.map((a) => a.valueCoding?.display || a.valueCoding?.code)
              .filter((item): item is string => Boolean(item)) || [],
          notNormalDetails:
            find(`${sectionLinkId}NotNormalDetails`)
              ?.answer?.map((a) => a.valueCoding?.display || a.valueCoding?.code)
              .filter((item): item is string => Boolean(item)) || [],
          notes: find(`${sectionLinkId}Notes`)?.answer?.[0]?.valueString || "",
        });
      }
    }
  }

  return answers;
}

export async function getGynecologicalPhysicalExamDiffHistory(
  medplum: MedplumClient,
  encounter: Encounter,
  versionId?: string,
  currentVersionIndex?: number,
): Promise<GynecologicalPhysicalExamDiffResult> {
  // Check cache first
  const cacheKey = generateCacheKey(encounter.id!, versionId, currentVersionIndex);
  const cachedEntry = diffCache.get(cacheKey);

  if (cachedEntry && isCacheValid(cachedEntry.timestamp)) {
    return cachedEntry.data;
  }

  const result: GynecologicalPhysicalExamDiffResult = {};

  // Fetch all gynecological physical exam histories in parallel
  await Promise.all(
    GYNECOLOGICAL_PHYSICAL_EXAMS.map(async (key) => {
      const url = getPhysicalExamUrl(key);
      if (!url) return;

      const [history, title] = await fetchAmendmentHistoryByQuestionnaireUrl(
        medplum,
        url,
        encounter.id!,
      );

      if (!history || !title) return;

      // Apply version filtering similar to regular physical exams
      let currentQuestionnaireResponse: QuestionnaireResponse | undefined;
      let previousQuestionnaireResponse: QuestionnaireResponse | undefined;

      if (versionId) {
        // Find the questionnaire response that matches the versionId via extension
        const currentIndex = history.findIndex((qr) => {
          const parentVersionId = getExtensionValue(
            qr,
            Extensions.QuestionnaireResponseParentVersionId,
          );
          return parentVersionId === versionId;
        });

        if (currentIndex === -1) {
          // If no matching version found, this means there are no physical exams
          // for this version, so we should show nothing
          currentQuestionnaireResponse = undefined;
          previousQuestionnaireResponse = undefined;
        } else {
          currentQuestionnaireResponse = history[currentIndex];
          // Find the most recent previous version that has physical exam data
          // Skip versions that don't have the parent version ID extension or have different parent versions
          previousQuestionnaireResponse = history.slice(currentIndex + 1).find((qr) => {
            const parentVersionId = getExtensionValue(
              qr,
              Extensions.QuestionnaireResponseParentVersionId,
            );
            return parentVersionId && parentVersionId !== versionId;
          });
        }
      } else if (currentVersionIndex !== undefined) {
        // Fallback to index-based lookup for backward compatibility
        const historyLength = history?.length;
        if (historyLength < currentVersionIndex + 1) return;

        currentQuestionnaireResponse = history[currentVersionIndex];
        previousQuestionnaireResponse = history[currentVersionIndex + 1] ?? history.at(-1);
      } else {
        // No version information provided, skip
        return;
      }

      // Process the filtered data into our normalized format
      // All physical exams use the same gynecological structure (normal/notNormal/declined)
      const currentAnswers = parseGynecologicalPhysicalExamAnswers(currentQuestionnaireResponse);
      const previousAnswers = parseGynecologicalPhysicalExamAnswers(previousQuestionnaireResponse);

      // Only add to result if we have valid data
      if (currentAnswers.length > 0 || previousAnswers.length > 0) {
        const zippedData = zip(currentAnswers, previousAnswers).filter(
          ([current, previous]) => current !== undefined || previous !== undefined,
        );

        if (zippedData.length > 0) {
          result[title] = zippedData;
        }
      }
      // If both current and previous are undefined, don't add anything to the result
    }),
  );

  // Store result in cache
  diffCache.set(cacheKey, {
    data: result,
    timestamp: Date.now(),
  });

  return result;
}
